// @ts-check
// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──
import { useEffect, useState } from "./00-boot.jsx";
import { CHAT_LANGUAGE_LABELS, CHAT_TRANSLATION_MEM, chatTranslationKey, fetchChatTranslation, langPick, localChatTranslation } from "./01-taxonomy-cities.jsx";
import { ACTIVE_LANG, tx } from "./04-i18n-dict.jsx";
// ⚠️ PAS d'import de 05-ui-atoms-theme ici : 01-taxonomy importe CE module ⇒ 02→05→01
//  serait un CYCLE (vu mordre le 29/08 : MAIN_CATS non initialisé à l'éval de 05 dans
//  le bundle du test search-intent). L'unique icône du fichier est un SVG local.
const LncClockSvg=({size=12,style={}})=><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{width:size,height:size,display:"inline-block",verticalAlign:"-2px",flexShrink:0,...style}}><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 7v5l3 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
function ChatTranslatedText({text,target,labels}){
  const readCache=()=>{
    const local=localChatTranslation(text,target);
    if(local) return {translated:local};
    return CHAT_TRANSLATION_MEM.get(chatTranslationKey(text,target))||null;
  };
  const [state,setState]=useState(readCache);
  useEffect(()=>{
    let alive=true;
    const cached=readCache();
    if(cached){setState(cached);return;}
    setState(null);
    fetchChatTranslation(text,target)
      .then(res=>{if(alive)setState(res||{error:true});})
      .catch(()=>{if(alive)setState({error:true});});
    return ()=>{alive=false;};
  },[text,target]);
  if(state&&state.translated) return state.translated;
  if(state&&state.error) return labels.unavailable;
  return labels.loading;
}
const chatLangName=(code,uiLang)=>((CHAT_LANGUAGE_LABELS[uiLang]||CHAT_LANGUAGE_LABELS.fr)?.[code]||code?.toUpperCase()||"");
const DEFAULT_RESERVATION_HOURS=12;
// Modèle « retrait seul » (2026-07-05) : plus de choix de durée dans l'UI — DEFAULT_RESERVATION_HOURS
// reste le plancher interne du calcul d'expiresAt (Lot D1). reservationDurationOptions supprimé.
const RESERVATION_PICKUP_GRACE_MS=2*3600000; // grâce après le créneau de retrait convenu
// ── Fuseau du RENDEZ-VOUS = celui de l'ANNONCE (le retrait/visite est physique sur place). Source = backend
// (colonne générée listings.timezone, dérivée du pays). Ces helpers interprètent/affichent l'heure murale
// dans un fuseau IANA quelconque (DST-aware) ; défaut Europe/Luxembourg = repli + rétro-compat (données CET).
const LUX_TZ="Europe/Luxembourg";
const _TZ_PARTS_CACHE={};
const _tzParts=(tz)=>_TZ_PARTS_CACHE[tz]||(_TZ_PARTS_CACHE[tz]=new Intl.DateTimeFormat("en-US",{timeZone:tz,hour12:false,year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit",second:"2-digit"}));
// Décalage (ms) du fuseau `tz` à l'instant `ts`.
const _tzOffsetMs=(ts,tz=LUX_TZ)=>{
  const p=_tzParts(tz).formatToParts(new Date(ts)).reduce((o,x)=>{o[x.type]=x.value;return o;},{});
  let h=+p.hour; if(h===24) h=0;
  return Date.UTC(+p.year,+p.month-1,+p.day,h,+p.minute,+p.second)-ts;
};
// Heure MURALE "YYYY-MM-DDTHH:MM" → epoch, interprétée dans le fuseau `tz` (celui de l'annonce), DST-aware.
const pickupToEpoch=(wall,tz=LUX_TZ)=>{
  const m=/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(wall==null?"":wall));
  if(!m){ const t=new Date(wall).getTime(); return Number.isFinite(t)?t:NaN; }
  const guess=Date.UTC(+m[1],+m[2]-1,+m[3],+m[4],+m[5]);
  const off=_tzOffsetMs(guess,tz); let ts=guess-off;
  const off2=_tzOffsetMs(ts,tz); if(off2!==off) ts=guess-off2;
  return ts;
};
// Entrée souple pour l'affichage : chaîne murale (sans fuseau → interprétée dans `tz`) OU instant absolu (Z/+hh:mm).
const _hasTZDesignator=s=>/[Zz]|[+-]\d\d:?\d\d$/.test(s);
const _displayEpoch=(iso,tz=LUX_TZ)=>{
  if(iso==null||iso==="") return NaN;
  if(typeof iso==="number") return Number.isFinite(iso)?iso:NaN; // epoch ms brut (compat : new Date(number))
  const s=String(iso);
  if(/^\d{10,}$/.test(s)) return Number(s);                       // epoch ms sous forme numérique
  return _hasTZDesignator(s)?new Date(s).getTime():pickupToEpoch(s,tz);
};
// Fuseau de l'APPAREIL (repère « chez vous »). Repli Lux si indisponible.
const _deviceTZ=()=>{ try{ return Intl.DateTimeFormat().resolvedOptions().timeZone||LUX_TZ; }catch(e){ return LUX_TZ; } };
// Le fuseau de l'annonce a-t-il un DÉCALAGE ≠ de l'appareil à l'instant `epoch` ? (par décalage, pas par nom :
// Berlin vs Paris = même décalage → pas de repère). C'est LA condition d'affichage du « = X chez vous ».
const tzDiffersFromDevice=(itemTz,epoch)=>{
  if(!itemTz||!Number.isFinite(epoch)) return false;
  try{ return _tzOffsetMs(epoch,itemTz)!==_tzOffsetMs(epoch,_deviceTZ()); }catch(e){ return false; }
};
// Libellé lisible du fuseau de l'annonce = nom du pays localisé (Intl.DisplayNames), repli ville IANA.
const tzZoneLabel=(country,tz,lang="fr")=>{
  try{ if(country){ const n=new Intl.DisplayNames([localeForLang(lang)],{type:"region"}).of(String(country).toUpperCase()); if(n) return n; } }catch(e){}
  return String(tz||"").split("/").pop().replace(/_/g," ");
};
// Lot « retrait + 2h » (2026-07-06, décision produit) : le plancher 12/24h est SUPPRIMÉ — la règle
// unique est expiresAt = retrait + 2h de grâce (l'acheteur peut « Reporter le retrait » s'il lui faut
// plus de temps ; relance near-expiry 1h avant). Miroir de la base (finalize_reserve_accept_listing, qui écrit l'échéance ; C5-2a).
// FILET : sans créneau futur exploitable (jamais le cas via l'UI — le formulaire exige un retrait),
// repli sur l'ancien délai de garde `hours` depuis `from` (aucune expiration aberrante possible).
const reservationExpiry=(hours,from=Date.now(),pickupAt=null,tz=LUX_TZ)=>{
  const base=from+Number(hours||DEFAULT_RESERVATION_HOURS)*3600000;
  const pk=pickupToEpoch(pickupAt,tz);
  return Number.isFinite(pk)&&pk>from?pk+RESERVATION_PICKUP_GRACE_MS:base;
};
const recentListingViewCount=(item,now=Date.now())=>{
  const recent=(item?.recentViewers||[]).filter(v=>now-Number(v.ts||0)<=30*60000);
  const unique=new Set(recent.map(v=>v.userId).filter(Boolean));
  return unique.size;
};
const hasMeaningfulTraffic=(item,now=Date.now())=>recentListingViewCount(item,now)>=2||Number(item?.views||0)>=2;
const sellerResponseBadge=(sellerId,convos=[],lang="fr")=>{
  const samples=[];
  let repliedToday=false;
  const dayStart=new Date();dayStart.setHours(0,0,0,0);
  convos.filter(c=>c.participants?.includes(sellerId)).forEach(c=>{
    const msgs=(c.messages||[]).filter(m=>m.from&&m.from!=="system").sort((a,b)=>(a.ts||0)-(b.ts||0));
    msgs.forEach((m,idx)=>{
      if(m.from===sellerId&&Number(m.ts||0)>=dayStart.getTime()) repliedToday=true;
      if(m.from===sellerId) return;
      const reply=msgs.slice(idx+1).find(x=>x.from===sellerId&&Number(x.ts||0)>Number(m.ts||0));
      if(reply) samples.push(Number(reply.ts||0)-Number(m.ts||0));
    });
  });
  if(samples.length){
    const avg=samples.reduce((s,x)=>s+x,0)/samples.length;
    const labels={
      minutes:{fr:"Répond en quelques minutes",en:"Replies in minutes",de:"Antwortet in Minuten",pt:"Responde em minutos"},
      today:{fr:"Répond aujourd'hui",en:"Replies today",de:"Antwortet heute",pt:"Responde hoje"},
      day:{fr:"Répond généralement sous 24h",en:"Usually replies within 24h",de:"Antwortet meist innerhalb von 24 Std.",pt:"Responde geralmente em 24h"}
    };
    if(avg<=20*60000) return labels.minutes[lang]||labels.minutes.fr;
    if(avg<=24*3600000) return repliedToday?(labels.today[lang]||labels.today.fr):(labels.day[lang]||labels.day.fr);
  }
  const labels={today:{fr:"Répond aujourd'hui",en:"Replies today",de:"Antwortet heute",pt:"Responde hoje"},day:{fr:"Répond généralement sous 24h",en:"Usually replies within 24h",de:"Antwortet meist innerhalb von 24 Std.",pt:"Responde geralmente em 24h"}};
  return repliedToday?(labels.today[lang]||labels.today.fr):(labels.day[lang]||labels.day.fr);
};
const localeForLang=lang=>({fr:"fr-FR",en:"en-US",de:"de-DE",pt:"pt-PT"}[lang]||"fr-FR");
const fmtReservationTime=(iso,lang=ACTIVE_LANG,tz=LUX_TZ)=>{ const e=_displayEpoch(iso,tz); return Number.isFinite(e)?new Date(e).toLocaleString(localeForLang(lang),{dateStyle:"short",timeStyle:"short",timeZone:tz}):""; };
// ── C7 · T7 (15/09/2026) · LE JOUR OÙ UNE CARTE QUI ATTEND SE CLÔTURERA D'ELLE-MÊME ────────────────────────────────────
//  La BASE inscrit sur la carte (data.closesAfter, recalculé à chaque écriture de la carte par le déclencheur
//  waiting_card_closes_after) l'instant à partir duquel la tâche resolve_stale_transactions peut la clôturer : dix jours
//  après la dernière étape enregistrée. La tâche passe à la minute 53 de chaque heure (planning en GMT) : la carte se
//  clôture à la PREMIÈRE passe qui suit. L'écran n'en dit que le JOUR, dans le fuseau de l'annonce — le même pour les deux
//  parties. La minute est épinglée au planning réel par le banc incident-carte-nee-au-serveur.
const AUTO_CLOSE_PASS_MINUTE=53;
const autoClosePassAt=closesAfter=>{
  const t=Number(closesAfter);
  if(!Number.isFinite(t)||t<=0) return null;
  const passe=Math.floor(t/3600000)*3600000+AUTO_CLOSE_PASS_MINUTE*60000;
  return passe>=t?passe:passe+3600000;
};
//  ⭐ C7 · T30, lot D (22/09/2026) — L'HORLOGE EST UN PARAMÈTRE. « L'année seulement si ce n'est pas celle-ci » se juge par
//   rapport à un MAINTENANT : il était lu en douce (new Date()), si bien que deux bancs qui disaient « 20 septembre » auraient
//   rougi le 1er janvier 2027 sans qu'une ligne change (l'écran, lui, dirait à raison « 20 septembre 2026 »). L'appelant passe
//   désormais SON instant (la carte : son tic de 30 s, le même qui décide « aujourd'hui ») ; par défaut, l'horloge réelle.
//   Et une date VIDE ne se dit pas : `Number(null)`, `Number("")` valent 0 — un instant fini, le 1er janvier 1970. Même bord
//   que l'heure (fmtAutoCloseHour, T29), qui l'avait déjà ; le jour ne l'avait pas.
const fmtAutoCloseDay=(at,lang=ACTIVE_LANG,tz=LUX_TZ,short=false,now=Date.now())=>{
  const t=Number(at);
  if(!Number.isFinite(t)||t<=0) return "";
  const dire=zone=>{
    const annee=d=>new Intl.DateTimeFormat("en-GB",{year:"numeric",timeZone:zone}).format(d);
    // Options écrites en place (typées par l'appel : month reste "short" | "long") ; l'année seulement si ce n'est pas celle-ci.
    return annee(new Date(t))!==annee(new Date(Number(now)))
      ?new Date(t).toLocaleDateString(localeForLang(lang),{day:"numeric",month:short?"short":"long",year:"numeric",timeZone:zone})
      :new Date(t).toLocaleDateString(localeForLang(lang),{day:"numeric",month:short?"short":"long",timeZone:zone});
  };
  try{return dire(tz||LUX_TZ);}catch(_){try{return dire(LUX_TZ);}catch(__){return "";}}
};
// ── C7 · T29 (20/09/2026) · LE DERNIER JOUR SE DIT EN HEURES, PAS EN DATE ─────────────────────────────
//  Une date au futur (« le 20 septembre ») LUE le 20 septembre se lit comme une échéance lointaine alors qu'il reste
//  quelques heures — et, la passe franchie, comme un mensonge. Le jour de la passe, l'écran dit l'HEURE. La question
//  « est-ce aujourd'hui ? » se pose dans le fuseau de l'ANNONCE, comme la date : le même jour pour les deux parties.
//  ⚠️ Comparer des dates, ce n'est PAS comparer des instants : on compare les deux QUANTIÈMES rendus dans ce fuseau.
const _dayKeyInTZ=(t,tz=LUX_TZ)=>{
  const n=Number(t);
  if(!Number.isFinite(n)) return "";
  const dire=zone=>new Intl.DateTimeFormat("en-CA",{year:"numeric",month:"2-digit",day:"2-digit",timeZone:zone}).format(new Date(n));
  try{return dire(tz||LUX_TZ);}catch(_){try{return dire(LUX_TZ);}catch(__){return "";}}
};
const isSameDayInTZ=(a,b,tz=LUX_TZ)=>{ const ka=_dayKeyInTZ(a,tz); return !!ka&&ka===_dayKeyInTZ(b,tz); };
const fmtAutoCloseHour=(at,lang=ACTIVE_LANG,tz=LUX_TZ)=>{
  //  ⚠️ `Number(null)` vaut 0, et 0 est un instant parfaitement fini : sans le second bord, « aucun instant »
  //   se serait dit « 01:00 » (l'origine des temps, à Luxembourg). Même bord que la passe elle-même.
  const t=Number(at);
  if(!Number.isFinite(t)||t<=0) return "";
  const dire=zone=>new Date(t).toLocaleTimeString(localeForLang(lang),{hour:"2-digit",minute:"2-digit",timeZone:zone});
  try{return dire(tz||LUX_TZ);}catch(_){try{return dire(LUX_TZ);}catch(__){return "";}}
};
const pad2=n=>String(n).padStart(2,"0");
const todayInputValue=()=>{
  const d=new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
};
const nowDateTimeInputValue=(offsetMinutes=15)=>{
  const d=new Date(Date.now()+offsetMinutes*60000);
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
// P2 : plafond du délai de retrait — une réservation ne doit pas geler une annonce indéfiniment.
const MAX_PICKUP_DAYS=30;
// Quota anti-accaparement : nb max de réservations ACTIVES par acheteur (chaque réservation gèle une annonce).
// Décision produit 2026-07-07 : 3 → 5 (3 trop court pour un acheteur actif ; le garde « 5 annulations récentes » reste).
const MAX_ACTIVE_RESERVATIONS=5;
// Garde anti-abus d'annulations : après RESERVE_CANCEL_MAX annulations RÉELLES sur une fenêtre glissante,
// l'acheteur ne peut plus créer de nouvelle réservation. Miroir EXACT du trigger serveur guard_reserve_cancel_quota
// (la loi = la base ; le client n'est qu'un pré-contrôle de confort). Les retraits bénins de demandes en attente
// (withdrawnPending) ne comptent JAMAIS ; la fenêtre rend le blocage temporaire.
const RESERVE_CANCEL_MAX=5;
const RESERVE_CANCEL_WINDOW_MS=30*24*3600*1000;
// Deux VOIES d'annulations (2026-07-11) : annuler un RDV/essai/visite à l'avance est bénin côté trust
// (franchise serveur rdv_cancel) — ça ne consomme donc PLUS le quota de RÉSERVATIONS. Les annulations
// de rendez-vous ont leur PROPRE jauge, qui ne bloque que les nouvelles demandes de RDV/essai/visite
// (nouveau garde serveur guard_visit_cancel_quota, 42904). Miroir EXACT des prédicats serveur
// is_counted_cancellation / is_counted_rdv_cancellation (la loi = la base ; ceci = confort UI).
const RDV_CANCEL_MAX=5;
const isRdvCancelData=d=>["visit","vehicle_rdv"].includes(d?.requestType||"")||["visit","vehicle_rdv"].includes(d?.sourceKind||"");
const countedCancellations=(convos,meId,rdv,now=Date.now())=>(convos||[]).flatMap(c=>c.messages||[]).filter(m=>m.from===meId&&m.kind==="reserveAction"&&m.status==="cancel"&&!m.data?.withdrawnPending&&(rdv?isRdvCancelData(m.data):!isRdvCancelData(m.data))&&(now-Number(m.ts||0))<RESERVE_CANCEL_WINDOW_MS).length;
// Garde anti-harcèlement par annonce : après LISTING_REQUEST_MAX demandes ÉCHOUÉES (réservation + visite + offre)
// de l'acheteur sur une même annonce dans la fenêtre, plus de nouvelle demande de réservation/visite. Miroir EXACT
// du trigger serveur guard_listing_request_cap (la loi = la base). Par annonce (l'acheteur reste libre ailleurs) ;
// fenêtre glissante → un échec ancien ne bloque plus à vie.
const LISTING_REQUEST_MAX=3;
const LISTING_REQUEST_WINDOW_MS=30*24*3600*1000;
const MAX_PICKUP_MS=MAX_PICKUP_DAYS*86400000;
const maxDateTimeInputValue=()=>{
  const d=new Date(Date.now()+MAX_PICKUP_MS);
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
// « now (+addMs) » en heure MURALE d'un fuseau donné → bornes min/max des sélecteurs alignées sur l'annonce
// (défaut Lux = rétro-compat). Sans ça, hors-CET le picker bornait sur l'heure APPAREIL (≤ qq h de décalage).
const wallNowInTZ=(tz=LUX_TZ,addMs=0)=>{
  const p=_tzParts(tz).formatToParts(new Date(Date.now()+addMs)).reduce((o,x)=>{o[x.type]=x.value;return o;},{});
  const h=(+p.hour===24?"00":String(+p.hour).padStart(2,"0"));
  return `${p.year}-${p.month}-${p.day}T${h}:${p.minute}`;
};
const isFutureLocalDateTime=(date,time,graceMs=60000)=>{
  if(!date||!time) return false;
  const ts=new Date(`${date}T${time}`).getTime();
  return Number.isFinite(ts)&&ts>=Date.now()-graceMs;
};
const fmtVisitSlot=(date,time,lang=ACTIVE_LANG,tz=LUX_TZ)=>date&&time?fmtReservationTime(`${date}T${time}`,lang,tz):"";
const reservationCountdown=(ts,lang=ACTIVE_LANG)=>{
  const diff=Number(ts||0)-Date.now();
  if(diff<=0) return tx("tax.expiree_ef42be",lang);
  const h=Math.floor(diff/3600000),m=Math.floor((diff%3600000)/60000);
  return h>0?`${h}h ${m}min`:`${Math.max(1,m)}min`;
};

// SPLIT-P3 : TzHint DÉPLACÉ ici depuis 11-messenger (son foyer naturel : tous ses helpers de fuseau vivent dans
//  ce module). Repère fuseau (EU) : n'affiche RIEN si le fuseau de l'annonce a le MÊME décalage que l'appareil
//  (cas CET courant → zéro changement visuel). Sinon, ligne discrète « <pays> · ≈ HH:MM chez vous ». VERBATIM.
function TzHint({wall,tz,country,lang,style}){
  const z=tz||LUX_TZ;
  const epoch=_displayEpoch(wall,z);
  if(!Number.isFinite(epoch)||!tzDiffersFromDevice(z,epoch)) return null;
  const zone=tzZoneLabel(country,z,lang);
  const local=new Date(epoch).toLocaleString(localeForLang(lang),{hour:"2-digit",minute:"2-digit",timeZone:_deviceTZ()});
  const chez=tx("tax.chez-vous_79cdff",lang);
  return <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--color-text-secondary)",fontWeight:"700",marginTop:3,minWidth:0,...(style||{})}}>
    <LncClockSvg size={12} style={{color:"var(--color-info)"}}/>
    <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{zone} · ≈ {local} {chez}</span>
  </div>;
}
export { ChatTranslatedText, DEFAULT_RESERVATION_HOURS, TzHint, LISTING_REQUEST_MAX, LISTING_REQUEST_WINDOW_MS, LUX_TZ, MAX_ACTIVE_RESERVATIONS, MAX_PICKUP_DAYS, MAX_PICKUP_MS, RDV_CANCEL_MAX, RESERVATION_PICKUP_GRACE_MS, RESERVE_CANCEL_MAX, RESERVE_CANCEL_WINDOW_MS, _TZ_PARTS_CACHE, _deviceTZ, _displayEpoch, _hasTZDesignator, _tzOffsetMs, _tzParts, autoClosePassAt, chatLangName, countedCancellations, fmtAutoCloseDay, fmtAutoCloseHour, fmtReservationTime, fmtVisitSlot, hasMeaningfulTraffic, isFutureLocalDateTime, isRdvCancelData, isSameDayInTZ, localeForLang, maxDateTimeInputValue, nowDateTimeInputValue, pad2, pickupToEpoch, recentListingViewCount, reservationCountdown, reservationExpiry, sellerResponseBadge, todayInputValue, tzDiffersFromDevice, tzZoneLabel, wallNowInTZ };
